<template>
  <modal
    :show="isVisible"
    :title="t('Entregar itens')"
    size="medium"
    elem-id="modal_pdv_entregas_deliver"
    data-testid="pdv-entregas-deliver-modal"
    :close-label="t('Cancelar')"
    close-test-id="pdv-entregas-deliver-modal-cancel-btn"
    :save-label="t('Entregar')"
    save-icon="pi pi-check-circle"
    save-test-id="pdv-entregas-deliver-modal-send-btn"
    :save-disabled-fn="saveDisabledFn"
    :save-fn="handleConfirmDelivery"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div v-if="isVisible" class="deliver-modal-body">
      <!-- Loading state -->
      <div v-if="isLoadingItems" class="loading-state">
        <i class="pi pi-spin pi-spinner"></i>
        {{ t('Carregando itens...') }}
      </div>

      <!-- Item list -->
      <div v-else-if="customerItems.length > 0" class="items-section">
        <p class="section-label">{{ t('Selecione os itens a entregar:') }}</p>
        <div
          v-for="row in sortedCustomerItems"
          :key="row.so_order_item_id"
          :data-testid="`pdv-entregas-deliver-item-${row.so_order_item_id}`"
          class="item-row"
          :class="{ 'item-row--disabled': row.item_status !== 'pronto' }"
        >
          <label class="item-label" :class="{ 'item-label--disabled': row.item_status !== 'pronto' }">
            <input
              type="checkbox"
              :data-testid="`pdv-entregas-deliver-item-checkbox-${row.so_order_item_id}`"
              :checked="selectedItemIds.has(row.so_order_item_id)"
              :disabled="row.item_status !== 'pronto'"
              @change="toggleItem(row.so_order_item_id, row.item_status)"
            />
            <span class="item-text">
              OS #{{ row.so_order_id }} · [{{ row.so_order_item_qrcode ?? '-' }}] {{ row.so_service_name ?? t('Item') }}
              <span v-if="row.item_status !== 'pronto'" class="item-reason">
                {{ statusReason(row.item_status) }}
              </span>
            </span>
          </label>
        </div>
      </div>

      <div v-else class="empty-state">
        {{ t('Nenhum item pendente para este cliente.') }}
      </div>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { error as logError, log } from '../../utils/DebugLogger';
import { useSOEntregas } from '../../composables/useSOEntregas';
import type { Orders_EntregasItemRow } from '../../contracts/Orders_EntregasItemRow';

const { t } = useI18n();
const { fetchEntregasItems, bulkDeliverItems } = useSOEntregas();

// ─── State ─────────────────────────────────────────────────────────────────

const isVisible = ref(false);
const isLoadingItems = ref(false);
const isSending = ref(false);
const customerId = ref<number | null>(null);
const triggerItemId = ref<number | null>(null);
const customerItems = ref<Orders_EntregasItemRow[]>([]);
const selectedItemIds = ref(new Set<number>());

// ─── Computed ──────────────────────────────────────────────────────────────

const sortedCustomerItems = computed(() => {
  return [...customerItems.value].sort((a, b) => {
    const aReady = a.item_status === 'pronto' ? 0 : 1;
    const bReady = b.item_status === 'pronto' ? 0 : 1;
    return aReady - bReady;
  });
});

// ─── Helpers ───────────────────────────────────────────────────────────────

function statusReason(status: string): string {
  switch (status) {
    case 'aguardando_laudo_entrada':
      return t('(Aguardando laudo de entrada)');
    case 'em_producao':
      return t('(Em produção)');
    case 'aguardando_laudo_saida':
      return t('(Aguardando laudo de saída)');
    default:
      return '';
  }
}

function toggleItem(id: number, status: string): void {
  if (status !== 'pronto') return;
  const next = new Set(selectedItemIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  selectedItemIds.value = next;
}

// ─── Save disabled ─────────────────────────────────────────────────────────

function saveDisabledFn(): boolean {
  return selectedItemIds.value.size === 0 || isSending.value;
}

// ─── handleConfirmDelivery ─────────────────────────────────────────────────

async function handleConfirmDelivery(): Promise<void> {
  if (selectedItemIds.value.size === 0) return;
  const ids = Array.from(selectedItemIds.value);
  isSending.value = true;
  try {
    await bulkDeliverItems(ids);
    const successText = ids.length === 1
      ? `1 ${t('item entregue')}`
      : `${ids.length} ${t('itens entregues')}`;
    eventBus.emit('globalMessage.success', successText);
    eventBus.emit('pdv-entregas.items.delivered');
    handleClose();
  } catch (err) {
    logError('PDVEntregasDeliveryModal', 'handleConfirmDelivery', 'bulk deliver failed', err);
    const msg = err instanceof Error ? err.message : (err as { message?: string })?.message;
    eventBus.emit('globalMessage.error', msg || t('Erro ao registrar entregas'));
  } finally {
    isSending.value = false;
  }
}

// ─── Open / Close ──────────────────────────────────────────────────────────

async function handleOpen(payload: { itemId: number; customerId: number }): Promise<void> {
  customerId.value = payload.customerId;
  triggerItemId.value = payload.itemId;
  isVisible.value = true;

  isLoadingItems.value = true;
  try {
    const all = await fetchEntregasItems();
    customerItems.value = all.filter((r) => r.so_order_client_id === payload.customerId);
    const triggerRow = customerItems.value.find((r) => r.so_order_item_id === payload.itemId);
    if (triggerRow && triggerRow.item_status === 'pronto') {
      selectedItemIds.value = new Set([payload.itemId]);
    } else {
      selectedItemIds.value = new Set();
    }
    log('PDVEntregasDeliveryModal', 'handleOpen', 'items loaded', {
      total: customerItems.value.length,
      preSelected: selectedItemIds.value.size,
    });
  } catch (err) {
    logError('PDVEntregasDeliveryModal', 'handleOpen', 'failed to fetch items', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar itens do cliente'));
  } finally {
    isLoadingItems.value = false;
  }
}

function handleClose(): void {
  isVisible.value = false;
  customerId.value = null;
  triggerItemId.value = null;
  customerItems.value = [];
  selectedItemIds.value = new Set();
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────

const onOpenEvent = (payload?: { itemId: number; customerId: number }): void => {
  if (!payload) return;
  void handleOpen(payload);
};

onMounted(() => {
  eventBus.on('modal.pdv-entregas-deliver.open', onOpenEvent);
  eventBus.on('modal.pdv-entregas-deliver.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.pdv-entregas-deliver.open', onOpenEvent);
  eventBus.off('modal.pdv-entregas-deliver.close', handleClose);
});
</script>

<style scoped>
.deliver-modal-body {
  padding: 8px 0;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--p-text-color, #374151);
  padding: 16px 0;
}

.section-label {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: var(--p-text-color, #374151);
}

.items-section {
  margin-bottom: 8px;
}

.item-row {
  padding: 6px 0;
  border-bottom: 1px solid var(--p-surface-200, #e5e7eb);
}

.item-row:last-child {
  border-bottom: none;
}

.item-row--disabled {
  opacity: 0.6;
}

.item-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.item-label--disabled {
  cursor: default;
}

.item-label input[type='checkbox'] {
  margin-top: 3px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.item-label--disabled input[type='checkbox'] {
  cursor: not-allowed;
}

.item-text {
  color: var(--p-text-color, #374151);
  line-height: 1.4;
}

.item-reason {
  margin-left: 4px;
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.85em;
}

.empty-state {
  color: var(--p-text-muted-color, #6b7280);
  padding: 12px 0;
}
</style>
